import React, { useCallback, useState, useContext, useMemo } from 'react'
import { Flex, Box } from 'reflexbox/styled-components'
import { Link } from 'react-router-dom'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import { ThemeContext } from 'styled-components'

import Breadcrumb from 'components/Breadcrumb'
import Card from 'components/Card'
import Button from 'components/Button'
import Loader from 'components/Loader'
import { Title, SemiTitle, Text } from 'components/Typography'
import { RadioGroup, TextField, SelectField, Dropzone, CheckboxField } from 'components/Form'

import { beneficiaryDocumentMask } from 'helpers/masks'
import { addBeneficiary, addGroupOfBeneficiaries } from 'services/beneficiaries'
import { attributeBeneficiary, beneficiariesAttribution } from 'services/groups'
import { getGroups } from 'services/groups'

const breadcrumbPath = [
  {
    label: 'Beneficiários',
    link: '/prepaid-purchase/beneficiaries',
  },
  {
    label: 'Adicionar beneficiário',
  },
]

const InitialValues = {
  form_type: 'single',
  csv: '',
  name: '',
  document: '',
  email: '',
  activate_user: false,
}

const validationSchema = Yup.object().shape({
  name: Yup.string().when('form_type', {
    is: value => value === 'single',
    then: Yup.string().required(),
    otherwise: Yup.string().notRequired(),
  }),
  document: Yup.string().when('form_type', {
    is: value => value === 'single',
    then: Yup.string()
      .min(14, 'CPF ou CNPJ inválido')
      .required(),
    otherwise: Yup.string().notRequired(),
  }),
  email: Yup.string().when('form_type', {
    is: value => value === 'single',
    then: Yup.string()
      .email()
      .required(),
    otherwise: Yup.string().notRequired(),
  }),
})

const New = () => {
  const theme = useContext(ThemeContext)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [csvError, setCsvError] = useState(null)
  const [paginationParams, setPaginationParams] = useState({ page: 1, limit: 6 })

  const dateOptions = useMemo(() => Array.from(Array(30).keys()).map(day => ({ label: day + 1, value: day + 1 })), [])

  const loadOptions = useCallback(
    async search => {
      if (search) {
        const { data } = await getGroups({ search })
        const formattedData = data.data.map(group => ({ label: group.name, value: group.id }))

        return {
          options: formattedData,
          hasMore: false,
        }
      } else {
        const { data } = await getGroups({ ...paginationParams })
        const formattedData = data.data.map(group => ({ label: group.name, value: group.id }))

        if (data.page !== data.total_pages) setPaginationParams({ page: data.page + 1, limit: data.limit })

        return {
          options: formattedData,
          hasMore: data.page === data.total_pages ? false : true,
        }
      }
    },
    [paginationParams],
  )

  const handleSubmit = useCallback(
    async ({ form_type, csv, name, email, document, groups, activation_day, activate_user }, { resetForm }) => {
      try {
        setIsSubmitting(true)

        if (form_type === 'group') {
          const formattedFile = new FormData()
          formattedFile.append('file', csv)

          toast.info('Cadastrando beneficiários do CSV...')

          const {
            data: { errors },
          } = await addGroupOfBeneficiaries(formattedFile)

          const attributionErrors = []

          if (groups) {
            for (let group of groups) {
              let { value: groupId, label } = group

              const response = await beneficiariesAttribution(groupId, formattedFile)

              attributionErrors.push(...response.data?.errors)
            }
          }

          if (errors.length) {
            toast.warn('Alguns dados do CSV não puderam ser inseridos.')

            const exception = new Error()

            exception.response = {
              data: errors,
            }

            throw exception
          }

          if (attributionErrors?.length) {
            toast.warn('Alguns beneficiários não puderam ser atribuições aos grupos')

            const exception = new Error()

            exception.response = {
              data: attributionErrors,
            }

            throw exception
          }

          toast.success('Beneficiários do CSV cadastrados com sucesso!')
        }

        if (form_type === 'single') {
          toast.info('Cadastrando beneficiário...')

          const formattedValues = activate_user
            ? {
                name,
                document,
                email,
                status: 'active',
              }
            : {
                name,
                document,
                email,
                status: activation_day?.value ? 'waiting' : 'inactive',
                ...(activation_day && { activation_day: activation_day.value }),
              }

          await addBeneficiary(formattedValues)

          toast.success('Cadastro realizado com sucesso!')

          if (groups) {
            for (let group of groups) {
              let { value: groupId, label } = group

              try {
                await attributeBeneficiary(groupId, formattedValues)
              } catch (e) {
                console.log(e)
                toast.error(`Não foi possível atribuir o beneficiário ao grupo ${label}`)
              }
            }
          }
        }

        resetForm()
      } catch (e) {
        console.log(e)
        if (form_type == 'single') {
          e?.response?.data?.document
            ? toast.error('CPF ou CNPJ inválidos')
            : toast.error('Não foi possivel fazer o cadastro no momento.')
        } else {
          const formattedError = e?.response?.data?.map(error => (
            <Flex>
              <SemiTitle color={theme.colors.error}>Linha {error.line}:&nbsp;</SemiTitle>
              <Text color={theme.colors.error}>{error.errors.map(log => log).join(' ')}</Text>
            </Flex>
          ))
          setCsvError(formattedError)
        }
      } finally {
        setIsSubmitting(false)
      }
    },
    [],
  )

  return (
    <Flex flexDirection='column'>
      <Breadcrumb children={breadcrumbPath} />
      <Title as='h3'>Adicionar beneficiário</Title>

      <Formik initialValues={InitialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
        {({ values, setFieldValue }) => (
          <Form>
            <Card mt={24}>
              <RadioGroup
                label=''
                mb={32}
                direction='row'
                name='form_type'
                options={[
                  { label: 'Apenas um beneficiário', value: 'single' },
                  { label: 'Diversos beneficiários', value: 'group' },
                ]}
              />
              <Flex flexDirection='column'>
                {values.form_type === 'single' ? (
                  <Box width={[1, '280px']}>
                    <TextField name='name' type='text' label='Nome do cliente' placeholder='Nome do cliente' mb={32} />
                    <TextField
                      name='document'
                      type='text'
                      label='CPF ou CNPJ'
                      placeholder='CPF ou CNPJ'
                      mask={beneficiaryDocumentMask}
                      guide={false}
                      mb={32}
                    />
                    <TextField name='email' type='email' label='E-mail' placeholder='E-mail' mb={32} />
                    <SelectField
                      name='activation_day'
                      placeholder='Dia de cobrança'
                      label='Dia de cobrança'
                      options={dateOptions}
                      mb={32}
                      disabled={values.activate_user}
                    />
                  </Box>
                ) : (
                  <Dropzone
                    setFieldValue={setFieldValue}
                    name='csv'
                    label='Upload da lista de clientes'
                    errors={csvError}
                    mb={32}
                  />
                )}
                <Box width={[1, '280px']}>
                  <SelectField
                    name='groups'
                    label='Selecionar Grupo'
                    placeholder='Selecionar grupo'
                    multiple
                    loadOptions={loadOptions}
                    mb={32}
                  />
                  {values.form_type === 'single' && (
                    <CheckboxField name='activate_user' label='Usuário ativo' mb={32} />
                  )}
                </Box>
              </Flex>
              <Flex width={[1, '560px']}>
                <Button
                  as={Link}
                  to='/prepaid-purchase/beneficiaries'
                  mr={24}
                  type='button'
                  outlined
                  style={{
                    flex: '1 0 280px',
                  }}
                >
                  Voltar
                </Button>
                <Button
                  type='submit'
                  disabled={isSubmitting}
                  style={{
                    flex: '1 0 280px',
                  }}
                >
                  {isSubmitting ? <Loader /> : 'Adicionar'}
                </Button>
              </Flex>
            </Card>
          </Form>
        )}
      </Formik>
    </Flex>
  )
}

export default New
